import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Progresso de fluxo em etapas: nome do bloco e "2 de 5" — nunca porcentagem.
 * Fios de 2px preenchidos até a etapa atual. Ink nos fluxos da clínica, plum na anamnese do paciente.
 */
export interface StepProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** Etapa atual, começando em 1. */
  current: number
  total: number
  /** Nome do bloco atual ("Sua saúde", "Escolha os documentos"). */
  label?: string
  tone?: 'ink' | 'plum'
}

export function StepProgress({
  current,
  total,
  label,
  tone = 'ink',
  className,
  ...props
}: StepProgressProps) {
  return (
    <div data-slot="step-progress" className={cn('flex flex-col gap-3', className)} {...props}>
      <div className="flex items-baseline justify-between gap-4">
        {label ? (
          <span className="font-display text-ink text-[13px] leading-[1.3] font-normal">
            {label}
          </span>
        ) : (
          <span />
        )}
        <span
          className="font-body text-eyebrow text-label font-light tracking-normal"
          aria-label={`Etapa ${current} de ${total}`}
        >
          {current} de {total}
        </span>
      </div>
      <div
        className="flex gap-1"
        role="progressbar"
        aria-label={
          label ? `${label}, etapa ${current} de ${total}` : `Etapa ${current} de ${total}`
        }
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={current}
      >
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-[2px] flex-1 transition-[background-color]',
              i < current ? (tone === 'plum' ? 'bg-plum' : 'bg-ink') : 'bg-hairline-strong',
            )}
          />
        ))}
      </div>
    </div>
  )
}
